import { useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useMaintenance } from "../context/MaintenanceContext";
import { Wrench, ShieldCheck, LogIn } from "lucide-react";

// Paths that remain accessible even when maintenance mode is ON,
// so the admin can still sign in and toggle it off.
const ALLOW_PATHS = ["/login", "/admin"];

export default function MaintenanceGate({ children }) {
  const { user, loading: authLoading } = useAuth();
  const { enabled, message, loading: maintLoading } = useMaintenance();
  const loc = useLocation();

  // Loading state — let it render through.
  if (authLoading || maintLoading) return children;

  if (!enabled) return children;

  // Admins always pass through (with a banner shown elsewhere).
  if (user && user.role === "admin") return children;

  // Login + admin entry are always reachable.
  if (ALLOW_PATHS.some((p) => loc.pathname === p || loc.pathname.startsWith(p + "/"))) {
    return children;
  }

  return (
    <div
      className="min-h-[calc(100vh-4rem)] grain relative overflow-hidden flex items-center justify-center p-6"
      data-testid="maintenance-gate"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(at 20% 0%, rgba(230,95,43,0.18) 0px, transparent 50%), radial-gradient(at 80% 100%, rgba(234,179,8,0.08) 0px, transparent 60%)",
        }}
      />
      <div className="relative max-w-2xl w-full text-center">
        <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-[#E65F2B]/10 border border-[#E65F2B]/30 mb-8">
          <Wrench className="h-8 w-8 text-[#E65F2B]" />
        </div>
        <p className="text-eyebrow text-[#EAB308] mb-4">
          <ShieldCheck className="inline h-3.5 w-3.5 mr-2 -mt-0.5" />
          Scheduled Maintenance
        </p>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]" data-testid="maintenance-title">
          We&apos;ll be <span className="text-[#E65F2B]">back</span> shortly.
        </h1>
        <p
          className="mt-8 text-lg text-white/70 leading-relaxed max-w-xl mx-auto whitespace-pre-wrap"
          data-testid="maintenance-message"
        >
          {message ||
            "Christoreel is briefly offline for scheduled maintenance. We'll be right back — thanks for your patience."}
        </p>

        <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
          <Link to="/login" className="ghost-btn text-base" data-testid="maintenance-signin-link">
            <LogIn className="h-4 w-4" /> Admin sign in
          </Link>
        </div>

        <p className="mt-16 text-xs text-white/30">
          Status: <span className="text-[#E65F2B]">degraded</span> · Christoreel · Naira-first
        </p>
      </div>
    </div>
  );
}
